/**
 * Unified Job Timeline
 * 
 * Aggregates all job events into a clean, chronological, day-grouped
 * timeline. Now includes automation execution events.
 * Read-only — no editing, no overriding.
 */

import { useMemo, useState, useEffect } from "react";
import { getJobAuditLog } from "@/services/job-operations.service";
import type { MaterialsAuditDiff } from "@/services/job-operations.service";
import type { Job, Issue, FieldMaterialItem } from "@/data/types";
import type { InstallerWrapRecord } from "@/data/installerWrapEngine";
import type { PurchaseOrder } from "@/data/VendorPOContext";
import type { JobAllocation } from "@/data/WarehouseInventoryContext";
import { OUTCOME_OPTIONS } from "@/data/installerWrapEngine";
import { useAutomationRunner } from "@/data/AutomationRunnerContext";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { UseUserResolverReturn } from "@/hooks/useUserResolver";
import { displayIdentity } from "@/lib/uuid";
import type { DbLead, DbAppointmentResult } from "@/types/database";
import type { ConsultationSessionData } from "@/services/consultation.service";
import type { BuildReviewEvent } from "@/data/buildReviewTypes";
import type { MarketplaceOffer } from "@/data/contractor/marketplaceOffer";
import { REVISION_REASON_LABELS } from "@/data/buildReviewTypes";
import {
  CheckCircle, AlertTriangle, DollarSign, Clock, Truck,
  Calendar, Wrench, FileText, CreditCard, Sunset,
  Package, Shield, Circle, Zap, XCircle, ShoppingBag,
  UserCheck, PhoneCall, Flag, ClipboardCheck, ArrowLeftRight,
} from "lucide-react";

// ── Event Types ──

type TimelineEventKind =
  | "stage_transition"
  | "wrap_submission"
  | "issue_created"
  | "issue_resolved"
  | "invoice_created"
  | "payment_received"
  | "scheduling"
  | "automation_success"
  | "automation_failure"
  | "po_created"
  | "po_submitted"
  | "po_confirmed"
  | "po_delivered"
  | "material_allocated"
  | "material_ready"
  | "field_material_posted"
  | "pipeline_lead_created"
  | "pipeline_stage_change"
  | "pipeline_appointment"
  | "pipeline_consultation_started"
  | "pipeline_consultation_completed"
  | "build_review_event"
  | "permit_submitted"
  | "permit_municipality_contact"
  | "permit_inspection"
  | "offer_event"
  | "closeout_event"
  | "phase_submitted"
  | "phase_qa_passed"
  | "phase_qa_failed"
  | "credit_issued"
  | "change_order_event"
  | "service_ticket_created"
  | "service_ticket_resolved"
  | "materials_list_updated"
  | "material_reallocated";

type EventPriority = "high" | "medium" | "low";
type FilterCategory = "all" | "pipeline" | "build_review" | "permitting" | "scheduling" | "offers" | "closeout" | "financial" | "field" | "issues" | "automation" | "ordering" | "materials" | "execution";

interface TimelineEvent {
  id: string;
  kind: TimelineEventKind;
  title: string;
  description: string;
  actor: string;
  timestamp: string;
  icon: React.ElementType;
  color: string;
  priority: EventPriority;
}

// ── Config ──

const KIND_STYLES: Record<TimelineEventKind, { icon: React.ElementType; color: string; priority: EventPriority }> = {
  stage_transition:        { icon: CheckCircle,    color: "text-primary",            priority: "low" },
  wrap_submission:         { icon: Sunset,          color: "text-warning",            priority: "medium" },
  issue_created:           { icon: AlertTriangle,   color: "text-destructive",        priority: "high" },
  issue_resolved:          { icon: Shield,          color: "text-success",            priority: "high" },
  invoice_created:         { icon: FileText,        color: "text-muted-foreground",   priority: "high" },
  payment_received:        { icon: CreditCard,      color: "text-success",            priority: "high" },
  scheduling:              { icon: Calendar,        color: "text-primary",            priority: "medium" },
  automation_success:      { icon: Zap,             color: "text-success",            priority: "medium" },
  automation_failure:      { icon: XCircle,         color: "text-destructive",        priority: "high" },
  po_created:              { icon: FileText,        color: "text-muted-foreground",   priority: "low" },
  po_submitted:            { icon: Truck,           color: "text-primary",            priority: "low" },
  po_confirmed:            { icon: CheckCircle,     color: "text-primary",            priority: "medium" },
  po_delivered:            { icon: Package,         color: "text-success",            priority: "high" },
  material_allocated:      { icon: Package,         color: "text-primary",            priority: "medium" },
  material_reallocated:    { icon: ArrowLeftRight,  color: "text-violet-500",          priority: "medium" },
  material_ready:          { icon: Shield,          color: "text-success",            priority: "high" },
  field_material_posted:   { icon: ShoppingBag,     color: "text-amber-500",          priority: "medium" },
  pipeline_lead_created:            { icon: UserCheck,    color: "text-sky-500",      priority: "medium" },
  pipeline_stage_change:            { icon: Flag,         color: "text-violet-500",   priority: "medium" },
  pipeline_appointment:             { icon: PhoneCall,    color: "text-blue-500",     priority: "high" },
  pipeline_consultation_started:    { icon: ClipboardCheck, color: "text-violet-500", priority: "medium" },
  pipeline_consultation_completed:  { icon: CheckCircle,  color: "text-emerald-500",  priority: "high" },
  build_review_event:               { icon: ClipboardCheck, color: "text-violet-500",  priority: "medium" },
  permit_submitted:                 { icon: FileText,        color: "text-violet-500",   priority: "high" },
  permit_municipality_contact:      { icon: PhoneCall,       color: "text-sky-500",      priority: "medium" },
  permit_inspection:                { icon: Shield,          color: "text-amber-500",    priority: "high" },
  offer_event:                      { icon: Zap,             color: "text-violet-500",   priority: "medium" },
  closeout_event:                   { icon: CheckCircle,     color: "text-emerald-500",  priority: "high" },
  phase_submitted:                  { icon: Wrench,          color: "text-violet-500",   priority: "medium" },
  phase_qa_passed:                  { icon: CheckCircle,     color: "text-emerald-500",  priority: "high" },
  phase_qa_failed:                  { icon: XCircle,         color: "text-rose-500",     priority: "high" },
  credit_issued:                    { icon: CreditCard,      color: "text-amber-500",    priority: "medium" },
  change_order_event:               { icon: FileText,        color: "text-violet-500",   priority: "medium" },
  service_ticket_created:           { icon: Wrench,          color: "text-amber-500",    priority: "high" },
  service_ticket_resolved:          { icon: CheckCircle,     color: "text-emerald-500",  priority: "high" },
  materials_list_updated:           { icon: Package,         color: "text-violet-500",   priority: "medium" },
};

const FILTER_KINDS: Record<FilterCategory, TimelineEventKind[] | null> = {
  all: null,
  pipeline: ["pipeline_lead_created", "pipeline_stage_change", "pipeline_appointment", "pipeline_consultation_started", "pipeline_consultation_completed"],
  build_review: ["build_review_event"],
  permitting: ["permit_submitted", "permit_municipality_contact", "permit_inspection"],
  offers: ["offer_event"],
  closeout: ["closeout_event"],
  financial: ["invoice_created", "payment_received", "credit_issued", "change_order_event"],
  field: ["wrap_submission", "stage_transition", "field_material_posted"],
  issues: ["issue_created", "issue_resolved", "service_ticket_created", "service_ticket_resolved"],
  scheduling: ["scheduling"],
  automation: ["automation_success", "automation_failure"],
  ordering: ["po_created", "po_submitted", "po_confirmed", "po_delivered"],
  materials: ["material_allocated", "material_ready", "materials_list_updated", "material_reallocated"],
  execution: ["phase_submitted", "phase_qa_passed", "phase_qa_failed"],
};

const FILTER_LABELS: { key: FilterCategory; label: string }[] = [
  { key: "all", label: "All" },
  { key: "pipeline", label: "Pipeline" },
  { key: "build_review", label: "Build Review" },
  { key: "permitting", label: "Permitting" },
  { key: "offers", label: "Offers" },
  { key: "closeout", label: "Closeout" },
  { key: "financial", label: "Financial" },
  { key: "field", label: "Field" },
  { key: "issues", label: "Issues" },
  { key: "scheduling", label: "Scheduling" },
  { key: "execution", label: "Execution" },
  { key: "ordering", label: "Ordering" },
  { key: "materials", label: "Materials" },
  { key: "automation", label: "Automation" },
];

const FILTER_EMPTY: Record<FilterCategory, string> = {
  all: "No timeline events yet.",
  pipeline: "No pipeline events for this job.",
  build_review: "No build review events for this job.",
  permitting: "No permitting events for this job.",
  offers: "No installer offers for this job.",
  closeout: "No closeout events for this job.",
  financial: "No financial events.",
  field: "No field events.",
  issues: "No issues recorded.",
  scheduling: "No scheduling events.",
  automation: "No automation activity.",
  execution: "No execution integrity events.",
  ordering: "No ordering events.",
  materials: "No material events.",
};

// ── Event Extraction ──

const PIPELINE_STAGE_ICON: Record<string, { icon: React.ElementType; color: string }> = {
  "Lead":                  { icon: UserCheck,    color: "text-sky-500" },
  "Quote Created":         { icon: FileText,     color: "text-violet-500" },
  "Appointment Scheduled": { icon: Calendar,     color: "text-amber-500" },
  "Appointment Completed": { icon: PhoneCall,    color: "text-blue-500" },
  "Follow-Up Needed":      { icon: Clock,        color: "text-orange-500" },
  "Closed Won":            { icon: CheckCircle,  color: "text-emerald-500" },
  "Closed Lost":           { icon: XCircle,      color: "text-rose-500" },
};

function extractEvents(
  job: Job,
  issues: Issue[],
  wrapRecords: InstallerWrapRecord[],
  purchaseOrders: PurchaseOrder[],
  jobAllocations: JobAllocation[],
  userResolver?: UseUserResolverReturn,
  fieldMaterials: FieldMaterialItem[] = [],
  linkedLead?: DbLead | null,
  appointmentResults: DbAppointmentResult[] = [],
  consultations: ConsultationSessionData[] = [],
  buildReviewEvents: BuildReviewEvent[] = [],
  permitDetail?: import("@/data/types").PermitDetail | null,
  schedulingEvents: Record<string, unknown>[] = [],
  installerOffers: MarketplaceOffer[] = [],
  phaseExecutions: { phaseName: string; status: string; completedAt?: string; completedBy?: string; qaReviewedAt?: string; qaReviewer?: string; qaFailReason?: string }[] = [],
  jobCredits: import("@/data/types").CustomerCredit[] = [],
  serviceTickets: import("@/data/serviceTicketTypes").ServiceTicket[] = [],
): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // ── Pipeline events ────────────────────────────────────────────────
  if (linkedLead) {
    // Lead created
    events.push({
      id: `pl-created-${linkedLead.id}`,
      kind: "pipeline_lead_created",
      title: "Lead Created",
      description: `${linkedLead.primary_name}${linkedLead.lead_source ? ` · ${linkedLead.lead_source}` : ""}`,
      actor: "System",
      timestamp: linkedLead.created_at,
      ...KIND_STYLES.pipeline_lead_created,
    });

    // Stage transitions from stage_history
    const stageHistory = Array.isArray(linkedLead.stage_history) ? linkedLead.stage_history as Array<{
      id: string; fromStage: string | null; toStage: string; changedAt: string; changedBy: string;
    }> : [];
    for (const change of stageHistory) {
      if (!change.toStage || !change.changedAt) continue;
      const stageCfg = PIPELINE_STAGE_ICON[change.toStage];
      const isHighStage = change.toStage === "Closed Won" || change.toStage === "Closed Lost" || change.toStage === "Appointment Scheduled";
      events.push({
        id: `pl-stage-${change.id}`,
        kind: "pipeline_stage_change",
        title: change.toStage,
        description: change.fromStage ? `From: ${change.fromStage}` : "Initial stage",
        actor: change.changedBy || "System",
        timestamp: change.changedAt,
        icon: stageCfg?.icon ?? Flag,
        color: stageCfg?.color ?? "text-muted-foreground",
        priority: isHighStage ? "high" : "medium",
      });
    }

    // Appointment results
    for (const appt of appointmentResults) {
      const statusLabel = appt.status.charAt(0).toUpperCase() + appt.status.slice(1).replace(/_/g, " ");
      const resultLabels: Record<string, string> = {
        booked: "Booked", not_interested: "Not Interested", needs_more_time: "Needs More Time",
        wrong_number: "Wrong Number", voicemail: "Voicemail", deferred: "Deferred",
      };
      const resultLabel = appt.result ? ` · ${resultLabels[appt.result] ?? appt.result}` : "";
      events.push({
        id: `pl-appt-${appt.id}`,
        kind: "pipeline_appointment",
        title: `Appointment ${statusLabel}`,
        description: `${appt.contact_name ?? linkedLead.primary_name}${resultLabel}`,
        actor: appt.created_by ?? "Sales",
        timestamp: appt.scheduled_at,
        icon: appt.status === "completed" ? PhoneCall : Calendar,
        color: appt.status === "completed" ? "text-blue-500" : "text-amber-500",
        priority: appt.status === "completed" ? "high" : "medium",
      });
    }

    // Consultation sessions
    for (const cs of consultations) {
      events.push({
        id: `cs-start-${cs.id}`,
        kind: "pipeline_consultation_started",
        title: "Consultation Started",
        description: `${cs.completed_steps?.length ?? 0} of 4 steps · ${cs.status}`,
        actor: cs.rep_id ? `Rep · …${cs.rep_id.slice(-6)}` : "Sales",
        timestamp: cs.started_at,
        ...KIND_STYLES.pipeline_consultation_started,
      });
      if (cs.completed_at) {
        events.push({
          id: `cs-done-${cs.id}`,
          kind: "pipeline_consultation_completed",
          title: "Consultation Completed",
          description: "Customer Info · Scope · Pricing · Review",
          actor: cs.rep_id ? `Rep · …${cs.rep_id.slice(-6)}` : "Sales",
          timestamp: cs.completed_at,
          ...KIND_STYLES.pipeline_consultation_completed,
        });
      }
    }
  }

  // ── Build Review events ──────────────────────────────────────────
  const BUILD_REVIEW_LABELS: Record<string, { label: string; priority: EventPriority; color: string }> = {
    submitted_for_review: { label: "Submitted for Review",       priority: "medium", color: "text-violet-500" },
    approved:             { label: "Build Review Approved",      priority: "high",   color: "text-emerald-500" },
    final_approved:       { label: "Final Approved",             priority: "high",   color: "text-emerald-500" },
    revision_requested:   { label: "Revision Requested",         priority: "high",   color: "text-orange-500" },
    sent_to_sales:        { label: "Sent to Sales",              priority: "medium", color: "text-sky-500" },
    sales_confirmed:      { label: "Sales Confirmed",            priority: "medium", color: "text-sky-500" },
    sales_resubmitted:    { label: "Resubmitted for Review",     priority: "medium", color: "text-violet-500" },
    change_order_created: { label: "Change Order Created",       priority: "high",   color: "text-amber-500" },
    customer_signed:      { label: "Customer Signed",            priority: "high",   color: "text-emerald-500" },
  };
  for (const ev of buildReviewEvents) {
    const cfg = BUILD_REVIEW_LABELS[ev.action] ?? { label: ev.action, priority: "medium" as EventPriority, color: "text-violet-500" };
    const isApproval = ev.action === "approved" || ev.action === "final_approved" || ev.action === "customer_signed";
    const isRevision = ev.action === "revision_requested";
    const detail = [
      ev.notes,
      ev.revisionNotes,
      ev.revisionReason ? REVISION_REASON_LABELS[ev.revisionReason] : null,
    ].filter(Boolean).join(" · ") || cfg.label;
    events.push({
      id: `br-${ev.id}`,
      kind: "build_review_event",
      title: cfg.label,
      description: detail,
      actor: ev.performedBy || "Ops",
      timestamp: ev.timestamp,
      icon: isApproval ? CheckCircle : isRevision ? AlertTriangle : ClipboardCheck,
      color: cfg.color,
      priority: cfg.priority,
    });
  }

  // ── Permitting events ───────────────────────────────────────────────
  if (permitDetail) {
    // Permit submitted
    if (permitDetail.submittedDate) {
      events.push({
        id: "permit-submitted",
        kind: "permit_submitted",
        title: "Permit Submitted",
        description: `${permitDetail.permitType}${permitDetail.municipality ? ` · ${permitDetail.municipality}` : ""}${permitDetail.permitNumber ? ` · #${permitDetail.permitNumber}` : ""}`,
        actor: permitDetail.assignedCoordinator || "Permitting",
        timestamp: permitDetail.submittedDate,
        ...KIND_STYLES.permit_submitted,
      });
    }

    // Municipality interactions
    const MUNICIPALITY_TYPE_LABELS: Record<string, string> = {
      call: "Municipality Call",
      email: "Municipality Email",
      portal_message: "Municipality Portal Message",
      in_person: "Municipality Visit",
      voicemail: "Municipality Voicemail",
    };
    const MUNICIPALITY_TYPE_ICONS: Record<string, React.ElementType> = {
      call: PhoneCall,
      email: FileText,
      portal_message: FileText,
      in_person: UserCheck,
      voicemail: PhoneCall,
    };
    for (const entry of permitDetail.municipalityLog ?? []) {
      events.push({
        id: `permit-muni-${entry.id}`,
        kind: "permit_municipality_contact",
        title: MUNICIPALITY_TYPE_LABELS[entry.type] ?? "Municipality Contact",
        description: [entry.summary, entry.contactPerson].filter(Boolean).join(" · "),
        actor: entry.loggedBy || "Permitting",
        timestamp: entry.createdAt,
        icon: MUNICIPALITY_TYPE_ICONS[entry.type] ?? PhoneCall,
        color: entry.type === "email" || entry.type === "portal_message" ? "text-violet-500" : "text-sky-500",
        priority: "medium",
      });
    }

    // Inspections
    for (const insp of permitDetail.inspectionLifecycle?.inspections ?? []) {
      if (insp.scheduledDate) {
        events.push({
          id: `permit-insp-sched-${insp.id}`,
          kind: "permit_inspection",
          title: `Inspection Scheduled: ${insp.inspectionType}`,
          description: insp.assignedOwner ? `Assigned to ${insp.assignedOwner}` : insp.inspectionType,
          actor: insp.assignedOwner || "Permitting",
          timestamp: insp.scheduledDate,
          icon: Calendar,
          color: "text-amber-500",
          priority: "medium",
        });
      }
      if (insp.completedDate) {
        const passed = insp.result === "passed";
        events.push({
          id: `permit-insp-done-${insp.id}`,
          kind: "permit_inspection",
          title: passed ? `Inspection Passed: ${insp.inspectionType}` : `Inspection Failed: ${insp.inspectionType}`,
          description: insp.failureReason || (passed ? "All items cleared" : insp.inspectionType),
          actor: insp.assignedOwner || "Permitting",
          timestamp: insp.completedDate,
          icon: passed ? CheckCircle : XCircle,
          color: passed ? "text-emerald-500" : "text-rose-500",
          priority: "high",
        });
      }
    }
  }

  // ── Installer Offer events ───────────────────────────────────────
  const OFFER_AUDIT_LABELS: Record<string, string> = {
    offer_created:    "Offer Sent",
    offer_accepted:   "Offer Accepted",
    offer_declined:   "Offer Declined",
    offer_expired:    "Offer Expired",
    offer_withdrawn:  "Offer Withdrawn",
    offer_superseded: "Offer Superseded",
    offer_extended: "Offer Extended",
    offer_window_changed: "Offer Window Changed",
  };
  for (const offer of installerOffers) {
    const contractorRef = `…${offer.contractorId.slice(-6)}`;
    const payout = offer.payoutAmountSnapshot != null ? ` · $${offer.payoutAmountSnapshot.toLocaleString()}` : "";
    for (const entry of offer.audit) {
      if (!entry.at) continue;
      const isAccepted = entry.event === "offer_accepted";
      const isDeclined = entry.event === "offer_declined" || entry.event === "offer_withdrawn";
      const reasonPart = entry.reason ? ` · ${entry.reason}` : "";
      events.push({
        id: `offer-${offer.id}-${entry.event}`,
        kind: "offer_event",
        title: OFFER_AUDIT_LABELS[entry.event] ?? entry.event,
        description: `${contractorRef}${entry.event === "offer_created" ? payout : ""}${reasonPart}`,
        actor: displayIdentity(userResolver?.getName(entry.actor) ?? entry.actor, "Dispatch"),
        timestamp: entry.at,
        icon: isAccepted ? CheckCircle : isDeclined ? XCircle : Zap,
        color: isAccepted ? "text-emerald-500" : isDeclined ? "text-rose-500" : "text-violet-500",
        priority: isAccepted || isDeclined ? "high" : "medium",
      });
    }
    // Fallback when audit trail is empty
    if (offer.audit.length === 0 && offer.sentAt) {
      events.push({
        id: `offer-sent-${offer.id}`,
        kind: "offer_event",
        title: "Offer Sent",
        description: `${contractorRef}${payout}`,
        actor: displayIdentity(userResolver?.getName(offer.sentBy) ?? offer.sentBy, "Dispatch"),
        timestamp: offer.sentAt,
        ...KIND_STYLES.offer_event,
      });
    }
  }

  // ── Closeout events (from job timestamped fields) ───────────────
  const signoff = job.closeoutSignoff;
  if (signoff?.signedAt) {
    events.push({
      id: "closeout-signoff",
      kind: "closeout_event",
      title: signoff.approved !== false ? "Customer Signed Off" : "Signoff Declined",
      description: [signoff.signerName, signoff.notes].filter(Boolean).join(" · ") || "Customer signoff recorded",
      actor: signoff.signerName || "Customer",
      timestamp: signoff.signedAt,
      icon: signoff.approved !== false ? CheckCircle : XCircle,
      color: signoff.approved !== false ? "text-emerald-500" : "text-rose-500",
      priority: "high",
    });
  }
  if (job.reviewSentAt) {
    events.push({
      id: "closeout-review-sent",
      kind: "closeout_event",
      title: "Review Request Sent",
      description: job.reviewSatisfied === true
        ? "Customer satisfied"
        : job.reviewSatisfied === false
          ? "Customer dissatisfied"
          : "Awaiting response",
      actor: "Ops",
      timestamp: job.reviewSentAt,
      icon: job.reviewSatisfied === true ? CheckCircle : job.reviewSatisfied === false ? AlertTriangle : Clock,
      color: job.reviewSatisfied === true ? "text-emerald-500" : job.reviewSatisfied === false ? "text-orange-500" : "text-muted-foreground",
      priority: "medium",
    });
  }
  if (job.referralRequestedAt) {
    events.push({
      id: "closeout-referral",
      kind: "closeout_event",
      title: "Referral Requested",
      description: job.referralReceived ? "Referral received" : "Awaiting referral",
      actor: "Ops",
      timestamp: job.referralRequestedAt,
      icon: job.referralReceived ? CheckCircle : UserCheck,
      color: job.referralReceived ? "text-emerald-500" : "text-sky-500",
      priority: "medium",
    });
  }
  if (job.contractorRatingAt) {
    events.push({
      id: "closeout-contractor-rating",
      kind: "closeout_event",
      title: "Contractor Rating Requested",
      description: job.contractorRatingCompleted ? "Rating completed" : "Awaiting rating",
      actor: "Ops",
      timestamp: job.contractorRatingAt,
      icon: job.contractorRatingCompleted ? CheckCircle : Shield,
      color: job.contractorRatingCompleted ? "text-emerald-500" : "text-amber-500",
      priority: "medium",
    });
  }

  // ── Phase execution events (Execution Integrity) ───────────────────
  const PHASE_LABELS: Record<string, string> = {
    prep: "Prep", demo: "Demo", rough: "Rough", install: "Install",
    finish: "Finish", qa: "QA", rework: "Rework", custom: "Custom",
  };
  for (const phase of phaseExecutions) {
    const label = PHASE_LABELS[phase.phaseName] ?? phase.phaseName;
    if (phase.completedAt) {
      events.push({
        id: `phase-submitted-${phase.phaseName}`,
        kind: "phase_submitted",
        title: `Phase Submitted: ${label}`,
        description: `Installer marked ${label} phase complete`,
        actor: displayIdentity(userResolver?.getName(phase.completedBy ?? "") ?? (phase.completedBy ?? ""), "Installer"),
        timestamp: phase.completedAt,
        ...KIND_STYLES.phase_submitted,
      });
    }
    if (phase.qaReviewedAt) {
      const passed = phase.status === "qa_passed";
      const failed = phase.status === "qa_failed";
      if (passed || failed) {
        events.push({
          id: `phase-qa-${phase.phaseName}`,
          kind: passed ? "phase_qa_passed" : "phase_qa_failed",
          title: passed ? `Phase QA Passed: ${label}` : `Phase QA Failed: ${label}`,
          description: failed && phase.qaFailReason ? phase.qaFailReason : `${label} phase QA ${passed ? "approved" : "rejected"}`,
          actor: displayIdentity(userResolver?.getName(phase.qaReviewer ?? "") ?? (phase.qaReviewer ?? ""), "QA"),
          timestamp: phase.qaReviewedAt,
          ...(passed ? KIND_STYLES.phase_qa_passed : KIND_STYLES.phase_qa_failed),
        });
      }
    }
  }

  // Stage transitions from activity history
  if (job.activityHistory) {
    job.activityHistory.forEach((entry, i) => {
      const isCompletion = (entry.action || "").toLowerCase().includes("complete");
      const resolved = userResolver?.getName(entry.user);
      const actorName = displayIdentity(resolved ?? entry.user, "System");
      events.push({
        id: `act-${i}`,
        kind: "stage_transition",
        title: entry.action || "Status Update",
        description: "",
        actor: actorName,
        timestamp: entry.date,
        icon: KIND_STYLES.stage_transition.icon,
        color: KIND_STYLES.stage_transition.color,
        priority: isCompletion ? "high" : "low",
      });
    });
  }

  // Installer wraps
  const jobWraps = wrapRecords.filter(r => r.jobId === job.id);
  jobWraps.forEach(wrap => {
    const outcomeLabel = OUTCOME_OPTIONS.find(o => o.value === wrap.outcome)?.label ?? wrap.outcome;
    events.push({
      id: `wrap-${wrap.id}`,
      kind: "wrap_submission",
      title: `End-of-Day Wrap: ${outcomeLabel}`,
      description: wrap.progressNotes || `${wrap.completionPercent}% complete`,
      actor: "Installer",
      timestamp: wrap.timestamp,
      ...KIND_STYLES.wrap_submission,
    });
  });

   // Issues
   const jobIssues = issues.filter(i => i.jobId === job.id);
   jobIssues.forEach(issue => {
     const issueActor = issue.source || displayIdentity(userResolver?.getName(issue.owner) ?? issue.owner, "System");
     events.push({
       id: `issue-create-${issue.id}`,
       kind: "issue_created",
       title: `Issue Created: ${issue.issueType}`,
       description: issue.description.length > 80 ? issue.description.slice(0, 80) + "…" : issue.description,
       actor: issueActor,
       timestamp: issue.dateCreated,
       ...KIND_STYLES.issue_created,
     });
     if (issue.status === "Resolved" || issue.status === "Closed") {
       const resolveActor = displayIdentity(userResolver?.getName(issue.owner) ?? issue.owner, "System");
       events.push({
         id: `issue-resolve-${issue.id}`,
         kind: "issue_resolved",
         title: `Issue Resolved: ${issue.issueType}`,
         description: "Resolved",
         actor: resolveActor,
         timestamp: issue.dateResolved || issue.dateCreated,
         ...KIND_STYLES.issue_resolved,
       });
     }
   });

  // Service Tickets
  for (const svc of serviceTickets) {
    events.push({
      id: `svc-created-${svc.id}`,
      kind: "service_ticket_created",
      title: `Service Ticket: ${svc.category}`,
      description: `${svc.subject} · ${svc.priority}`,
      actor: displayIdentity(userResolver?.getName(svc.createdBy) ?? svc.createdBy, "Ops"),
      timestamp: svc.createdAt,
      icon: svc.priority === "Urgent" ? AlertTriangle : Wrench,
      color: svc.priority === "Urgent" ? "text-rose-500" : svc.priority === "High" ? "text-amber-500" : "text-muted-foreground",
      priority: (svc.priority === "Urgent" || svc.priority === "High") ? "high" : "medium",
    });
    if (svc.resolvedAt) {
      events.push({
        id: `svc-resolved-${svc.id}`,
        kind: "service_ticket_resolved",
        title: `Service Ticket Resolved: ${svc.category}`,
        description: svc.resolution ? svc.resolution.slice(0, 80) : svc.subject,
        actor: displayIdentity(userResolver?.getName(svc.resolvedBy ?? "") ?? (svc.resolvedBy ?? ""), "Ops"),
        timestamp: svc.resolvedAt,
        ...KIND_STYLES.service_ticket_resolved,
      });
    }
  }

  // Invoices
  const invoices = job.financials?.invoices ?? [];
  invoices.forEach(inv => {
    events.push({
      id: `inv-${inv.id}`,
      kind: "invoice_created",
      title: `Invoice: ${inv.type.charAt(0).toUpperCase() + inv.type.slice(1)}`,
      description: `$${inv.amount.toLocaleString()}${inv.dueDate ? ` · Due ${new Date(inv.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}`,
      actor: "System",
      timestamp: inv.issuedAt || inv.dueDate || new Date().toISOString(),
      ...KIND_STYLES.invoice_created,
    });
  });

  // Payments
  const payments = job.financials?.payments ?? [];
  payments.forEach(p => {
    events.push({
      id: `pay-${p.id}`,
      kind: "payment_received",
      title: "Payment Received",
      description: `$${p.amount.toLocaleString()} via ${p.method}`,
      actor: "System",
      timestamp: p.timestamp,
      ...KIND_STYLES.payment_received,
    });
  });

  // Change orders
  for (const co of job.changeOrders ?? []) {
    if (co.createdAt) {
      events.push({
        id: `co-created-${co.id}`,
        kind: "change_order_event",
        title: `Change Order CO-${co.orderNumber} Created`,
        description: `${co.summary} · ${co.changeAmount >= 0 ? "+" : ""}$${co.changeAmount.toLocaleString()}`,
        actor: co.createdBy || "Ops",
        timestamp: co.createdAt,
        ...KIND_STYLES.change_order_event,
      });
    }
    if (co.signedAt) {
      events.push({
        id: `co-signed-${co.id}`,
        kind: "change_order_event",
        title: `Change Order CO-${co.orderNumber} Signed`,
        description: `Revised total $${co.revisedContractValue.toLocaleString()}`,
        actor: co.homeownerSignature ? "Customer" : "Rep",
        timestamp: co.signedAt,
        icon: CheckCircle,
        color: "text-emerald-500",
        priority: "high",
      });
    }
  }

  // Credits
  const CREDIT_TYPE_LABEL: Record<string, string> = { service_credit: "Service Credit", discount: "Discount Credit", rework_credit: "Rework Credit", goodwill: "Goodwill Credit", refund: "Refund" };
  for (const cr of jobCredits) {
    events.push({
      id: `cr-${cr.id}`,
      kind: "credit_issued",
      title: CREDIT_TYPE_LABEL[cr.creditType] ?? "Credit Issued",
      description: `$${cr.creditAmount.toLocaleString()} · ${cr.reasonCategory}${cr.status === "approved" ? "" : ` · ${cr.status}`}`,
      actor: cr.createdBy || "Ops",
      timestamp: cr.dateCreated,
      icon: cr.status === "approved" ? CreditCard : Clock,
      color: cr.status === "approved" ? "text-emerald-500" : "text-amber-500",
      priority: "medium",
    });
  }

  // Scheduling events from scheduling_events table (rich history)
  if (schedulingEvents.length > 0) {
    const SCHED_TYPE_LABEL: Record<string, string> = {
      install: "Installation Scheduled", inspection: "Inspection Scheduled",
      followup: "Follow-up Scheduled", delivery: "Delivery Scheduled",
      consultation: "Consultation Scheduled", sales_appointment: "Sales Appointment Scheduled",
      service_call: "Service Call Scheduled", pre_install: "Pre-Install Scheduled",
      schedule_set: "Schedule Set", schedule_removed: "Schedule Removed",
    };
    const SCHED_STATUS_LABEL: Record<string, string> = {
      draft: "Draft", scheduled: "Scheduled", confirmed: "Confirmed",
      in_progress: "In Progress", completed: "Completed", cancelled: "Cancelled",
    };
    for (const ev of schedulingEvents) {
      const eventType = String(ev.event_type ?? "");
      const status = String(ev.status ?? "scheduled");
      const startAt = String(ev.start_at ?? "");
      const createdAt = String(ev.created_at ?? "");
      if (!createdAt) continue;
      const dateStr = startAt
        ? new Date(startAt).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
        : "";
      const statusPart = status && status !== "scheduled" ? SCHED_STATUS_LABEL[status] ?? status : "";
      const notesPart = ev.notes ? String(ev.notes).slice(0, 60) : "";
      const isCompleted = status === "completed";
      const isCancelled = status === "cancelled";
      const isInstall = eventType === "install";
      events.push({
        id: `sched-ev-${ev.id}`,
        kind: "scheduling",
        title: SCHED_TYPE_LABEL[eventType] ?? "Event Scheduled",
        description: [dateStr, statusPart, notesPart].filter(Boolean).join(" · "),
        actor: displayIdentity(userResolver?.getName(String(ev.created_by ?? "")) ?? String(ev.created_by ?? ""), "Scheduling"),
        timestamp: createdAt,
        icon: isCompleted ? CheckCircle : isCancelled ? XCircle : Calendar,
        color: isCompleted ? "text-emerald-500" : isCancelled ? "text-rose-500" : "text-primary",
        priority: isInstall || isCompleted ? "high" : "medium",
      });
    }
  } else if (job.scheduledInstallDate) {
    // Fallback: only scheduledInstallDate available (no scheduling_events rows yet)
    events.push({
      id: "sched-install",
      kind: "scheduling",
      title: "Install Scheduled",
      description: `${job.assignedCrew || "Crew TBD"} · ${new Date(job.scheduledInstallDate).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}`,
      actor: "System",
      timestamp: job.scheduledInstallDate,
      ...KIND_STYLES.scheduling,
    });
  }

  // Purchase Orders
  for (const po of purchaseOrders) {
    const totalLabel = po.totalCost > 0 ? ` · $${po.totalCost.toLocaleString()}` : "";
    if (po.createdAt) {
      events.push({
        id: `po-create-${po.poId}`,
        kind: "po_created",
        title: `PO Created: ${po.poNumber}`,
        description: `${po.vendorName} · ${po.items.length} item${po.items.length !== 1 ? "s" : ""}`,
        actor: "Procurement",
        timestamp: po.createdAt,
        ...KIND_STYLES.po_created,
      });
    }
    if (po.submittedAt) {
      events.push({
        id: `po-submit-${po.poId}`,
        kind: "po_submitted",
        title: `PO Submitted: ${po.poNumber}`,
        description: `${po.vendorName}${totalLabel}`,
        actor: "Procurement",
        timestamp: po.submittedAt,
        ...KIND_STYLES.po_submitted,
      });
    }
    if (po.confirmedAt) {
      events.push({
        id: `po-confirm-${po.poId}`,
        kind: "po_confirmed",
        title: `PO Confirmed: ${po.poNumber}`,
        description: `${po.vendorName}${po.confirmedEta ? ` · ETA ${new Date(po.confirmedEta).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}`,
        actor: po.vendorName,
        timestamp: po.confirmedAt,
        ...KIND_STYLES.po_confirmed,
      });
    }
    if (po.deliveredAt) {
      events.push({
        id: `po-deliver-${po.poId}`,
        kind: "po_delivered",
        title: `Materials Delivered: ${po.poNumber}`,
        description: `${po.vendorName}${totalLabel}`,
        actor: po.vendorName,
        timestamp: po.deliveredAt,
        ...KIND_STYLES.po_delivered,
      });
    }
  }

  // Warehouse Allocations
  for (const a of jobAllocations) {
    if (!a.allocatedAt) continue;
    if (a.status === "Ready for Install") {
      events.push({
        id: `alloc-ready-${a.id}`,
        kind: "material_ready",
        title: "Material Ready for Install",
        description: `${a.productName || a.sku} · ${a.quantity} unit${a.quantity !== 1 ? "s" : ""}`,
        actor: a.allocatedBy || "Warehouse",
        timestamp: a.allocatedAt,
        ...KIND_STYLES.material_ready,
      });
    } else if (a.status === "Reserved") {
      events.push({
        id: `alloc-reserve-${a.id}`,
        kind: "material_allocated",
        title: "Material Allocated",
        description: `${a.productName || a.sku} · ${a.quantity} unit${a.quantity !== 1 ? "s" : ""} reserved`,
        actor: a.allocatedBy || "Warehouse",
        timestamp: a.allocatedAt,
        ...KIND_STYLES.material_allocated,
      });
    }
  }

  // Field material posted events
  for (const fm of fieldMaterials) {
    if (!fm.inventoryPostedAt) continue;
    const qty = `${fm.quantity} unit${fm.quantity !== 1 ? "s" : ""}`;
    const skuPart = fm.sku ? ` · SKU ${fm.sku}` : "";
    events.push({
      id: `fm-posted-${fm.id}`,
      kind: "field_material_posted",
      title: "Field Material Posted",
      description: `${fm.itemName}${skuPart} · ${qty}`,
      actor: fm.inventoryPostedBy || "Field",
      timestamp: fm.inventoryPostedAt,
      ...KIND_STYLES.field_material_posted,
    });
  }

  // Sort chronologically (newest first)
  events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return events;
}

// ── Day Grouping ──

function groupByDay(events: TimelineEvent[]): { label: string; date: string; events: TimelineEvent[] }[] {
  const groups = new Map<string, TimelineEvent[]>();

  events.forEach(e => {
    const dateKey = new Date(e.timestamp).toISOString().slice(0, 10);
    if (!groups.has(dateKey)) groups.set(dateKey, []);
    groups.get(dateKey)!.push(e);
  });

  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  return Array.from(groups.entries()).map(([date, evts]) => ({
    label: date === today ? "Today" : date === yesterday ? "Yesterday" : new Date(date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    date,
    events: evts,
  }));
}

// ── Priority Styles ──

const PRIORITY_OPACITY: Record<EventPriority, string> = {
  high: "",
  medium: "",
  low: "opacity-60",
};

// ── Component ──

interface Props {
  job: Job;
  issues: Issue[];
  wrapRecords: InstallerWrapRecord[];
  purchaseOrders?: PurchaseOrder[];
  jobAllocations?: JobAllocation[];
  fieldMaterials?: FieldMaterialItem[];
  userResolver?: UseUserResolverReturn;
  linkedLead?: DbLead | null;
  appointmentResults?: DbAppointmentResult[];
  consultations?: ConsultationSessionData[];
  buildReviewEvents?: BuildReviewEvent[];
  permitDetail?: import("@/data/types").PermitDetail | null;
  schedulingEvents?: Record<string, unknown>[];
  installerOffers?: MarketplaceOffer[];
  phaseExecutions?: { phaseName: string; status: string; completedAt?: string; completedBy?: string; qaReviewedAt?: string; qaReviewer?: string; qaFailReason?: string }[];
  jobCredits?: import("@/data/types").CustomerCredit[];
  serviceTickets?: import("@/data/serviceTicketTypes").ServiceTicket[];
  refreshToken?: number;
}

export function UnifiedJobTimeline({ job, issues, wrapRecords, purchaseOrders = [], jobAllocations = [], fieldMaterials = [], userResolver, linkedLead, appointmentResults = [], consultations = [], buildReviewEvents = [], permitDetail, schedulingEvents = [], installerOffers = [], phaseExecutions = [], jobCredits = [], serviceTickets = [], refreshToken }: Props) {
  const [filter, setFilter] = useState<FilterCategory>("all");
  const { automationTimelineEvents } = useAutomationRunner();

  const [materialsAuditEvents, setMaterialsAuditEvents] = useState<TimelineEvent[]>([]);

  useEffect(() => {
    let cancelled = false;
    getJobAuditLog(job.id).then((entries) => {
      if (cancelled) return;
      const events: TimelineEvent[] = [];
      for (const entry of entries) {
        const fields = (entry.changedFields as Record<string, unknown> | null);

        // Re-allocation entry
        const realloc = fields?.reallocation as { direction: "in" | "out"; otherJobLabel: string; sku: string; productName: string; qty: number } | undefined;
        if (realloc) {
          const isOut = realloc.direction === "out";
          events.push({
            id: `mat-audit-${entry.id}`,
            kind: "material_reallocated",
            title: isOut ? "Materials Re-allocated Out" : "Materials Re-allocated In",
            description: isOut
              ? `${realloc.qty} × ${realloc.productName || realloc.sku} moved to ${realloc.otherJobLabel}`
              : `${realloc.qty} × ${realloc.productName || realloc.sku} received from ${realloc.otherJobLabel}`,
            actor: entry.editedBy ?? "Warehouse",
            timestamp: entry.createdAt,
            ...KIND_STYLES.material_reallocated,
          });
          continue;
        }

        // Materials list diff entry
        const diff = fields?.materials_list as MaterialsAuditDiff | undefined;
        if (!diff) continue;
        const parts: string[] = [];
        if (diff.added?.length) parts.push(`${diff.added.length} added`);
        if (diff.removed?.length) parts.push(`${diff.removed.length} removed`);
        if (diff.changed?.length) parts.push(`${diff.changed.length} changed`);
        if (parts.length === 0) continue;
        events.push({
          id: `mat-audit-${entry.id}`,
          kind: "materials_list_updated",
          title: "Materials Updated",
          description: parts.join(", "),
          actor: entry.editedBy ?? "Ops",
          timestamp: entry.createdAt,
          ...KIND_STYLES.materials_list_updated,
        });
      }
      setMaterialsAuditEvents(events);
    });
    return () => { cancelled = true; };
  }, [job.id, refreshToken]);

  const allEvents = useMemo(() => {
    const baseEvents = extractEvents(job, issues, wrapRecords, purchaseOrders, jobAllocations, userResolver, fieldMaterials, linkedLead, appointmentResults, consultations, buildReviewEvents, permitDetail, schedulingEvents, installerOffers, phaseExecutions, jobCredits, serviceTickets);

    // Inject automation timeline events for this job
    const jobAutoEvents = automationTimelineEvents
      .filter(e => e.jobId === job.id)
      .map(e => ({
        id: e.id,
        kind: (e.metadata?.success ? "automation_success" : "automation_failure") as TimelineEventKind,
        title: e.subject || "Automation",
        description: e.message,
        actor: "Automation",
        timestamp: e.timestamp,
        ...KIND_STYLES[e.metadata?.success ? "automation_success" : "automation_failure"],
      }));

    return [...baseEvents, ...jobAutoEvents, ...materialsAuditEvents].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [job, issues, wrapRecords, automationTimelineEvents, userResolver, linkedLead, appointmentResults, consultations, buildReviewEvents, permitDetail, schedulingEvents, installerOffers, phaseExecutions, jobCredits, serviceTickets, materialsAuditEvents]);

  const filteredEvents = useMemo(() => {
    const kinds = FILTER_KINDS[filter];
    if (!kinds) return allEvents;
    return allEvents.filter(e => kinds.includes(e.kind));
  }, [allEvents, filter]);

  const groups = useMemo(() => groupByDay(filteredEvents), [filteredEvents]);

  // Count per filter for badges
  const counts = useMemo(() => {
    const c: Record<FilterCategory, number> = { all: allEvents.length, pipeline: 0, build_review: 0, permitting: 0, scheduling: 0, offers: 0, closeout: 0, financial: 0, field: 0, issues: 0, automation: 0, ordering: 0, materials: 0, execution: 0 };
    for (const e of allEvents) {
      for (const [key, kinds] of Object.entries(FILTER_KINDS)) {
        if (kinds && (kinds as TimelineEventKind[]).includes(e.kind)) c[key as FilterCategory]++;
      }
    }
    return c;
  }, [allEvents]);

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex items-center gap-1 flex-wrap">
        {FILTER_LABELS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={cn(
              "px-2.5 py-1 text-[10px] font-medium rounded-full transition-colors",
              filter === f.key
                ? "bg-primary text-primary-foreground"
                : "bg-muted/50 text-muted-foreground hover:bg-muted"
            )}
          >
            {f.label}
            {counts[f.key] > 0 && (
              <span className={cn(
                "ml-1 tabular-nums",
                filter === f.key ? "text-primary-foreground/70" : "text-muted-foreground/60"
              )}>
                {counts[f.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {filteredEvents.length === 0 ? (
        <div className="text-center py-8">
          <Clock className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">{FILTER_EMPTY[filter]}</p>
        </div>
      ) : (
        /* Timeline */
        <div className="space-y-0">
          {groups.map((group) => (
            <div key={group.date}>
              {/* Day header */}
              <div className="sticky top-0 z-10 flex items-center gap-2 py-2 bg-background/95 backdrop-blur-sm">
                <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                  {group.label}
                </span>
                <div className="flex-1 h-px bg-border/60" />
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  {group.events.length}
                </span>
              </div>

              {/* Events */}
              <div className="relative ml-3">
                <div className="absolute left-[7px] top-0 bottom-0 w-px bg-border/50" />

                {group.events.map((event) => {
                  const Icon = event.icon;
                  const isHigh = event.priority === "high";
                  const isAutomation = event.kind === "automation_success" || event.kind === "automation_failure";
                  return (
                    <div
                      key={event.id}
                      className={cn(
                        "relative flex gap-3 pb-4 last:pb-2",
                        PRIORITY_OPACITY[event.priority]
                      )}
                    >
                      {/* Dot */}
                      <div className={cn(
                        "relative z-10 flex items-center justify-center rounded-full bg-background shrink-0 mt-0.5",
                        isHigh ? "h-5 w-5 border-2 border-current" : "h-4 w-4 border border-border/60",
                        isHigh && event.color
                      )}>
                        <Icon className={cn(
                          event.color,
                          isHigh ? "h-3 w-3" : "h-2.5 w-2.5"
                        )} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <p className={cn(
                            "text-xs leading-tight",
                            isHigh ? "font-semibold text-foreground" : "font-medium text-foreground"
                          )}>{event.title}</p>
                          {isAutomation && (
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-[8px] h-3.5 px-1",
                                event.kind === "automation_failure" ? "border-destructive/40 text-destructive" : "border-success/40 text-success"
                              )}
                            >
                              {event.kind === "automation_failure" ? "FAILED" : "AUTO"}
                            </Badge>
                          )}
                          <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                            {new Date(event.timestamp).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                          </span>
                        </div>
                        {event.description && (
                          <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{event.description}</p>
                        )}
                        <p className="text-[10px] text-muted-foreground/60 mt-0.5">{event.actor}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
